import React, { SyntheticEvent } from 'react';
import { Product_product, adminAddProductImagesVariables, adminUpdateProductMainImageVariables, productImagesById, productImagesById_adminProducts_data_images } from '@api/graphql/types';
import ProductImageField, { ProductImage } from './ProductImageField';
import GraphQLClient from '@api/graphql/GraphQLClient';
import * as Mutations from '@api/graphql/mutations';
import * as Queries from '@api/graphql/queries';
import { compose } from 'react-apollo';
import { injectIntl, InjectedIntlProps } from 'react-intl';
import ImageModal from '@components/ImageModal';
import DeleteButton from '@components/DeleteButton';
import { DispatchProp, connect } from 'react-redux';
import { ToastActionType } from '@actions/toastActions';
import { ToastLevelType } from '@components/Toast/Toast';
import { Loading } from '@components/Loading';

interface Props {
    product: Product_product;
}

interface State {
    modal: {
        visible: boolean;
        url: string;
    };
    newImage: ProductImage;
    defaultImage: ProductImage;
    isLoading: boolean;
    images: productImagesById_adminProducts_data_images[];
}

class ProductImageForm extends React.Component<Props & InjectedIntlProps & DispatchProp, State> {
    public state: State = {
        isLoading: false,
        modal: {
            visible: false,
            url: '',
        },
        newImage: {
            file_name: '',
            base64_image: '',
        },
        defaultImage: {
            file_name: '',
            base64_image: '',
        },
        images: [],
    };

    public componentDidMount() {
        this.fetchProductImages();
    }

    private fetchProductImages = () => {
        this.setState({isLoading: true}, () => {
            const variables = {ext_id: this.props.product.ext_id};
            GraphQLClient.query<productImagesById>({
                query: Queries.productImagesById,
                variables,
            }).then((response) => {
                if (response.data && response.data.adminProducts && response.data.adminProducts.data.length) {
                    const product = response.data.adminProducts.data[0];
                    this.setState({images: product.images || [], isLoading: false});
                }
            }).catch((error) => {
                this.feedBack('error', ToastLevelType.error);
                this.setState({isLoading: false});
            });
        });
    }

    private renderSortButtons = (image: productImagesById_adminProducts_data_images) => {
        const index = (this.state.images as productImagesById_adminProducts_data_images[]).indexOf(image);
        const isFirst = index === 1;
        const isLast = index === this.state.images!.length - 1;
        return (
            <div className="col-sm-2">
                {!isFirst && <a className="pull-left" onClick={this.moveImage(image, -1)}><i className="fa fa-chevron-up" /></a>}
                {!isLast && <a className="pull-right" onClick={this.moveImage(image, 1)}><i className="fa fa-chevron-down" /></a>}
            </div>
        );
    }

    private moveImage = (image: productImagesById_adminProducts_data_images, direction: -1 | 1) => (e: SyntheticEvent) => {
        const index = this.state.images!.indexOf(image);
        const ids = [...this.state.images!].slice(1).map((img) => img.id);
        const temp = ids[index + direction];
        ids[index + direction] = ids[index];
        ids[index] = temp;

        GraphQLClient.mutate({
            mutation: Mutations.adminSortProductImages,
            variables: {
                product_id: this.props.product.id,
                image_ids: ids.filter((id) => !!id),
            },
        }).then(() => {
            this.feedBack('move.success', ToastLevelType.success);
        }).catch((error: any) => {
            this.feedBack('error', ToastLevelType.error);
        });

    }

    private feedBack(key: string, type: ToastLevelType) {
        this.fetchProductImages();
        this.props.dispatch({type: ToastActionType.addToast, toast: {
            id: `${key}-${type}`,
            text: this.props.intl.formatMessage({id: `module.product.notification.${key}`}),
            type,
            timeout: 2000,
        }});
    }

    private renderImage(image: productImagesById_adminProducts_data_images, isDefault: boolean = false) {
        const src = image.conversions.thumbnail || image.conversions.list || image.conversions.full || image.url;
        return (
            <div className="row" key={image.id}>
                <div className="col-sm-4">
                    <a onClick={() => { this.setState({modal: {visible: true, url: image.url}}); }}>
                        <img src={src} />
                    </a>
                </div>
                <div className="col-sm-4">
                    {!isDefault && this.renderSortButtons(image)}
                </div>
                <div className="col-sm-4">
                    {!isDefault && (
                        <DeleteButton
                            className="btn-block"
                            onClick={this.deleteImage}
                            value={image.id}
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderDefaultImage() {
        const defaultImage: productImagesById_adminProducts_data_images = this.state.images[0];
        if (defaultImage && defaultImage.conversions && defaultImage.conversions.thumbnail) {
            return (
                <div className="row">
                    <h4>{this.props.intl.formatMessage({id: 'module.product.defaultImage.label'})}</h4>
                    {this.renderImage(defaultImage, true)}
                    <ProductImageField
                        label={this.props.intl.formatMessage({id: 'module.product.defaultImage.change.label'})}
                        name="defaultImage"
                        value={this.state.defaultImage}
                        onChange={this.onChange('defaultImage')}
                        onSave={this.changeDefaultImage}
                    />

                </div>
            );
        }
        return (
            <div className="row">
                {this.props.intl.formatMessage({id: 'module.product.noDefaultImage'})}
            </div>
        );
    }

    private onChange = (name: keyof State) => (value: ProductImage) => {
        const state = {...this.state, [name]: value};
        this.setState(state);
    }

    private addImage = (value: ProductImage) => {
        const variables: adminAddProductImagesVariables = {
            product_id: this.props.product.id,
            images: [
                value,
            ],
        };

        return GraphQLClient.mutate({
            mutation: Mutations.adminAddProductImages,
            variables,
        }).then(() => {
            this.feedBack('upload.success', ToastLevelType.success);
        }).catch((error: any) => {
            this.feedBack('error', ToastLevelType.error);
        });
    }

    private changeDefaultImage = (value: ProductImage) => {
        const variables: adminUpdateProductMainImageVariables = {
            product_id: this.props.product.id,
            ...value,
        };

        GraphQLClient.mutate({
            mutation: Mutations.adminUpdateProductMainImage,
            variables,
        }).then(() => {
            this.feedBack('change.success', ToastLevelType.success);
        }).catch((error: any) => {
            this.feedBack('error', ToastLevelType.error);
        });
    }

    private deleteImage = (id: string) => {
        GraphQLClient.mutate({
            mutation: Mutations.adminDeleteProductImages,
            variables: {
                product_id: this.props.product.id,
                image_ids: [id],
            },
        }).then(() => {
            this.feedBack('delete.success', ToastLevelType.success);
        }).catch((error: any) => {
            this.feedBack('error', ToastLevelType.error);
        });
    }

    private renderAddForm() {
        return (
            <div className="row">
                <ProductImageField
                    label={this.props.intl.formatMessage({id: 'module.product.image.add.label'})}
                    name="newImage"
                    productId={this.props.product.id}
                    value={this.state.newImage}
                    onChange={this.onChange('newImage')}
                    onSave={this.addImage}
                />
            </div>
        );
    }

    private renderImages() {
        const { images } = this.state;
        if (images.length > 1) {
                const additionalImages = [...images].slice(1);
                return (
                    <div className="row">
                        <h4>{this.props.intl.formatMessage({id: 'module.product.images.label'})}</h4>
                        {additionalImages.map((image) => this.renderImage(image))}
                    </div>
                );
        }
        return null;
    }

    public render() {
        if (this.state.isLoading) {
            return <Loading />;
        }

        return (
            <>
                {this.renderDefaultImage()}
                <hr />
                {this.renderImages()}
                <hr />
                {this.renderAddForm()}
                <ImageModal
                    url={this.state.modal.url}
                    visible={this.state.modal.visible}
                    onClose={() => {
                        this.setState({modal: {visible: false, url: ''}});
                    }}
                />
            </>
        );
    }
}


export default compose(
    injectIntl,
    connect(null),
)(ProductImageForm);




// WEBPACK FOOTER //
// ./src/modules/Product/components/ProductImageForm.tsx